import { AuthState, OrdersState } from '@/store/types';
import { DateTime } from 'luxon';
import { orderBy } from 'lodash';
import { valueSort } from './sort';
import { decodeLocalization, decodeString } from './decoding';
import { isFalseyButNotZero } from './general';
import { state as authState } from '../store/auth/auth';
import { localize, resolveLocale } from './i18n-helpers';
import { getCountryFromAddress } from './currency';
import { parsePhoneNumberFromString } from 'libphonenumber-js/min';
import i18n from '@/i18n';

// =============================================================================
// Orders
// =============================================================================

/**
 * Format the order before submitting to the backend
 *
 * @param {OrdersState} state
 * @param {AuthState} authState
 * @return {any}
 */
export default function formatOrder(state: OrdersState, authState: AuthState): any {
	const { cart, checkout, costs, orderDetails } = state;
	const { restaurant } = authState;

	// - If any item is NOT replenish/extend, then menuManagerEvent = false
	// - Only if ALL items are replenish/extend, then menuManagerEvent = true
	const allItemsAreReplenishExtend = cart.items.length && cart.items.every((item: OrderItem) => item.isReplenishExtend);
	const menuManagerEventForOrder = orderDetails.menuManagerEvent && allItemsAreReplenishExtend;

	return {
		config: {
			restaurantId: restaurant.app8_restaurant,
			employeeId: authState.employeeId,
			createdBy: authState.createdBy,
			...(orderDetails.type === 'dine-in' && { tableNum: orderDetails.tableNum }),
			tableInfo: {
				tableNum: orderDetails.tableNum,
				tableArea: orderDetails.tableArea,
				tableLocation: orderDetails.tableLocation,
				checkNum: orderDetails.checkNum
			},
			menuManagerEvent: orderDetails.menuManagerEvent,
			paidItemEvent: !menuManagerEventForOrder,
			menuManagerEventBEO: orderDetails.menuManagerEventBEO,
			menuManagerEventPackage: orderDetails.menuManagerEventPackage,
			menuManagerLaneReservation: orderDetails.menuManagerLaneReservation,
			isKiosk: authState.role === 'kiosk'
		},
		items: cart.items.map(({ cartIndex, ...rest }) => rest),
		costs: costs,
		checkout: {
			pickup: {
				delivery: checkout.pickup?.delivery ?? false,
				notes: checkout.pickup?.notes ?? null
			},
			delivery: checkout.pickup?.delivery && checkout.delivery?.type ? checkout.delivery : null,
			no_takeout_timeslot: restaurant.order_options?.no_takeout_timeslot ?? false,
			questions: checkout.questions?.length ? checkout.questions : null,
			contact: {
				full_name: checkout.contact.full_name,
				email: checkout.contact.email,
				phone_number: checkout.contact.phone_number
			},
			memberInfo: checkout.memberInfo && checkout.memberInfo.id
				? { id: checkout.memberInfo.id, identifier: checkout.memberInfo.identifier, provider: checkout.memberInfo.provider, promosApplied: checkout.memberInfo.promosApplied ? Array.from(checkout.memberInfo.promosApplied) : null, promos: checkout.memberInfo.promos }
				: null,
			invoice: checkout.invoice ?? false,
			costCenter: checkout.costCenter ?? null,
			purchaseOrder: checkout.purchaseOrder ?? null,
			autoAccept: checkout.autoAccept ?? false,
			cateringEvent: checkout.cateringEvent,
			quoteInfo: checkout.quoteInfo ? {
				quoteBehavior: checkout.quoteInfo.quoteBehavior,
				quoteExpiresAt: checkout.quoteInfo.quoteExpiresAt || undefined,
				...(checkout.quoteInfo.quoteBehavior === 'requireDeposit' && {
					type: checkout.quoteInfo.type,
					amount: checkout.quoteInfo.amount,
					percentageValue: checkout.quoteInfo.type === 'percentage' ? checkout.quoteInfo.percentageValue : undefined
				})
			} : null
		},
		date: DateTime.local().toISO(),
		isMenuManagerOrder: true
	};
}

// =============================================================================
// Menu, sections, items, etc
// =============================================================================

export function formatSection(menuSection: MenuSection): MenuSection {
	menuSection = {
		id: menuSection.id ? menuSection.id : undefined,
		name: menuSection.name,
		slug: menuSection.slug,
		description: menuSection.description,
		type: 'a-la-carte',
		price: formatValue(+menuSection.price, '0.00', 'price') as string,
		localization: menuSection.localization ? menuSection.localization : undefined
	};
	return menuSection;
}

export function formatMenu(menu: Menu): Menu {
	menu = {
		id: menu.id ? menu.id : undefined,
		allow_sections: menu.allow_sections,
		image: menu.image,
		description: menu.description,
		dine_in: menu.dine_in,
		max_orders_per_interval: menu.max_orders_per_interval ? parseInt((menu.max_orders_per_interval as any)) : 0,
		name: menu.name,
		on_demand: menu.on_demand,
		ordering_interval: menu.ordering_interval,
		last_call_time: menu.last_call_time,
		estimated_prep_time: menu.estimated_prep_time,
		price: formatValue(+menu.price, '0.00', 'price') as string,
		required_notice: menu.required_notice,
		scheduled: menu.scheduled,
		member_only: menu.member_only,
		event_package: menu.event_package,
		access_level: menu.access_level ? menu.access_level : 'unrestricted',
		slug: menu.slug,
		type: 'a-la-carte',
		localization: menu.localization ? menu.localization : undefined,
		published_at: menu.published_at
	};
	return menu;
}

export function formatItem(menuItem: MenuItem): MenuItem {
	menuItem = {
		id: menuItem.id,
		section_id: menuItem.section_id ? menuItem.section_id : undefined,
		menu_id: menuItem.menu_id,
		name: menuItem.name,
		description: menuItem.description,
		ingredients: menuItem.ingredients,
		nutritional_sheet_url: menuItem.nutritional_sheet_url,
		slug: menuItem.slug,
		sku: menuItem.sku,
		category: menuItem.category,
		calories: menuItem.calories,
		base_price: menuItem.base_price || null,
		price: formatValue(+menuItem.price, '0.00', 'price') as string,
		price_unit: menuItem.price_unit,
		price_type: menuItem.price_type,
		image: menuItem.image,
		includes: menuItem.includes,
		allergens: menuItem.allergens,
		diets: menuItem.diets,
		is_hidden: menuItem.is_hidden,
		sold_out: menuItem.sold_out,
		featured_item: menuItem.featured_item,
		upsell_item: menuItem.upsell_item,
		meal_package: menuItem.meal_package,
		min_quantity: menuItem.min_quantity,
		price_on_request: menuItem.price_on_request,
		auto_restock: menuItem.auto_restock,
		alcoholic_beverage: menuItem.alcoholic_beverage,
		order: menuItem.order,
		additional_prices: menuItem.additional_prices,
		price_schedule: menuItem.price_schedule,
		tax_rebate_eligibility: menuItem.tax_rebate_eligibility,
		tax_rate: menuItem.tax_rate,
		localization: menuItem.localization ? menuItem.localization : undefined,
		published_at: menuItem.published_at
	};
	return menuItem;
}

export function formatOptionGroup (addOnOptionGroup: OptionGroup): OptionGroup {
	return {
		id: addOnOptionGroup.id ? addOnOptionGroup.id : undefined,
		name: addOnOptionGroup.name,
		type: addOnOptionGroup.type || 'addons',
		allow_multiple_selection: !!addOnOptionGroup.allow_multiple_selection,
		required: !!addOnOptionGroup.required,
		limit: typeof addOnOptionGroup.limit === 'number' ? addOnOptionGroup.limit : null,
		description: addOnOptionGroup.description,
		custom_quantities: addOnOptionGroup.custom_quantities,
		localization: addOnOptionGroup.localization ? addOnOptionGroup.localization : undefined
	};
}

export function formatOrderItem(item: MenuItem | OrderItem): MenuOrderItem {
	return {
		id: item.id,
		menu_id: item.menu_id,
		section_id: item.section_id,
		name: item.name,
		localization: item.localization,
		slug: item.slug,
		sku: item.sku ? item.sku : null,
		category: item.category ? item.category : null,
		price: item.price,
		orderPrice: item.orderPrice ?? +(item.price || 0),
		alcoholic_beverage: item.alcoholic_beverage,
		tax_rate: item.tax_rate,
		tax_rebate_eligibility: item.tax_rebate_eligibility ? item.tax_rebate_eligibility : 'eligible',
		quantity: item.quantity ? item.quantity : 1,
		description: item.description,
		ingredients: item.ingredients,
		price_unit: item.price_unit,
		price_type: item.price_type,
		image: item.image,
		auto_restock: item.auto_restock,
		price_on_request: item.price_on_request,
		order: item.order,
		meal_package: item.meal_package,
		nutritional_sheet_url: item.nutritional_sheet_url,
		orderOptions: item.options ? item.options.map((optionGroup: OptionGroup) => formatOrderOptionGroup(optionGroup)) : [],
		tags: item.tags
	};
}

/**
* Formats the option group to the OrderOptionGroup type
*
* @param {OptionGroup} optionGroup
* @return {OrderOptionGroup}
*/
export function formatOrderOptionGroup(optionGroup: OptionGroup): OrderOptionGroup {
	return {
		id: optionGroup.id,
		name: optionGroup.name,
		description: optionGroup.description ? decodeString(optionGroup.description) : null,
		localization: optionGroup.localization ? decodeLocalization(optionGroup.localization) : optionGroup.localization,
		optionGroupSku: optionGroup.sku ? optionGroup.sku : null,
		optionGroupType: optionGroup.type,
		requiredOption: optionGroup.required,
		multipleOption: optionGroup.allow_multiple_selection,
		customQuantities: optionGroup.custom_quantities,
		limit: optionGroup.limit || null,
		order: optionGroup.order,
		memo: false,
		values: optionGroup.values ? formatOrderOptions(optionGroup.values) : []
	};
}

/**
* Formats the option group to the OrderOptionGroup type
*
* @param {Option[]} options
* @return {OrderOption[]}
*/
export function formatOrderOptions(options: Option[]): OrderOption[] {
	return options.map((option: Option) => {
		return {
			id: option.id,
			name: option.name,
			description: option.description ? decodeString(option.description) : null,
			localization: option.localization ? decodeLocalization(option.localization) : option.localization,
			calories: option.calories,
			product_id: option.product_id ? option.product_id : null,
			sku: option.sku ? option.sku : null,
			category: option.category ? option.category : null,
			price: option.price,
			tax_rate: option.tax_rate ? option.tax_rate : null,
			promos: option.promos,
			roll_up: option.roll_up,
			order: option.order,
			quantity: typeof option.quantity === 'number' ? option.quantity : 0,
			max_quantity: option.max_quantity,
			default_selection: option.default_selection,
			options: option.sub_group
				? [formatOrderOptionGroup(option.sub_group)]
				: option.options
					? option.options.map((optionGroup: OptionGroup) => formatOrderOptionGroup(optionGroup))
					: []
		};
	});
}

/**
 * Format the order options for comparision to increase quantity or not. We are formatting time
 * to compare them with JSON.stringify. The reason we are doing this is because we have multiple
 * identifiers that may or may not change in the future, this is the easiest way to future proof
 * any changes.
 *
 * @param {OrderOptionGroup[] | null | undefined} orderOptions
 * @return {Omit<OrderOptionGroup, 'price'>[] | undefined}
 */
export function formatOrderOptionsToCompare(orderOptions: OrderOptionGroup[] | null | undefined): OrderOptionGroup[] | undefined {
	if(orderOptions?.length) {
		// Order option groups
		const tempOrderOptionGroups = orderOptions!.map((orderOptionGroup: OrderOptionGroup) => {

			// Options
			const tempOptions = orderOptionGroup.values!.map((option: OrderOption) => {
				let tempSubOptionGroups = option.options;

				// Only compare sub options if there are selected sub options
				const hasSelectedSubOptions = tempSubOptionGroups?.some((subOptionGroup: OrderOptionGroup) => subOptionGroup.values.length);

				// Sub options
				if (hasSelectedSubOptions) {
					tempSubOptionGroups = formatOrderOptionsToCompare(tempSubOptionGroups);
				}

				// Return options
				return {
					id: option.id,
					sku: option.sku,
					name: option.name,
					localization: option.localization,
					options: hasSelectedSubOptions ? tempSubOptionGroups : undefined,
					quantity: option.quantity,
					price: option.price
				};
			});

			// Return order option group
			return {
				id: orderOptionGroup.id,
				name: orderOptionGroup.name,
				optionGroupType: orderOptionGroup.optionGroupType,
				optionGroupSku: orderOptionGroup.optionGroupSku,
				localization: orderOptionGroup.localization,
				memo: orderOptionGroup.memo,
				values: tempOptions
			};
		});
		return tempOrderOptionGroups;
	}
	return undefined;
}


// =============================================================================
// Staff ordering
// =============================================================================

/**
 * Sorts the options by order or price and parse the options to the correct format
 *
 * @param {OptionGroup[]} options
 */
function parseAndSortOptions(options: OptionGroup[]): void {
	// If the option group has an order, we have to sort them, otherwise keep going
	if(options.some(optionGroup => optionGroup.order )) {
		options.sort((a: OptionGroup, b: OptionGroup) => valueSort(a, b, 'order'));
	}
	options.map((optionGroup: OptionGroup) => {
		if(optionGroup.values?.length) {

			// Verify if the default price matches its scheduled price
			for (let index = 0; index < optionGroup.values.length; index++) {
				let option = optionGroup.values[index];

				option = getPriceAndAvailability(option) as Option;

				option.calories = Number.isInteger(option.calories) && option.calories! >= 0 ? option.calories : null;
				option.quantity = 0;
			}

			// If the options has an order, we have to sort them by it, otherwise sort
			// by price.
			if(optionGroup.values.some(option => option.order )) {
				optionGroup.values.sort((a: Option, b: Option) => valueSort(a, b, 'order'));
			}
			else {
				optionGroup.values.sort((a: Option, b: Option) => valueSort(a, b, 'price'));
			}

			// Repeat if more options (Treats it as an object, transform into an array for recursivity)
			// Also manipulation this right here in case we wanna change the structure to having more than
			// one option group per options, it'll be an easier change if we treat it already as an array.
			optionGroup.values.map((option: any) => {
				// If for some reason roll up isn't present, we set it to true as it is its default
				if(typeof option.roll_up === 'undefined') {
					option.roll_up = true;
				}

				// We have to support both the old and new format of options
				if(option.options && option.options.name) {
					option.options = [option.options];
					parseAndSortOptions(option.options);
				}
				else if(option.sub_group) {
					option.options = [option.sub_group];
					parseAndSortOptions(option.options);
				}
			});
		}
	});
}

/**
 * Sort the options by order (DESC) and then price (ASC) if the order is not present
 *
 * @param {OptionGroup[] | undefined} options
 * @return {OptionGroup[] | undefined}
 */
function sortOption(options: OptionGroup[] | undefined): OptionGroup[] | undefined {
	if(options?.length) {

		// Sort the options by order (DESC) and then price (ASC) if the order is not present
		parseAndSortOptions(options);

		// Put the pricing option into its own array to be treated differently
		const pricingOption = options.find(option => option.type === 'pricing');
		if(pricingOption) {
			const addonsOption = options.filter(option => option.type !== 'pricing');
			const newOptionArray = [pricingOption, ... addonsOption];
			return newOptionArray;
		}
	}
	return options;
}

/**
 * Check if the item is available and update the price if there is a scheduled price
 *
 * @param {MenuItem | Option} item
 * @return {MenuItem | Option}
 */
function getPriceAndAvailability(item: MenuItem | Option): MenuItem | Option {
	const dateObj: DateTime = DateTime.local();
	// Get the item's schedule of the day
	const dayOfWeek: number = dateObj.weekday;

	if (item.price_schedule?.length) {
		const itemDaySchedule = item.price_schedule[dayOfWeek - 1];

		// Instantiate the date that will be used for scheduling to beginning of the day
		const today = dateObj.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
		const todayDiff = dateObj.diff(today, ['minutes']);
		const scheduleIndex = Math.floor(todayDiff.minutes / 15);

		// We validate the item price
		if(itemDaySchedule) {
			if(item.additional_prices && Object.keys(item.additional_prices)) {
				const itemPriceKey: string = itemDaySchedule[scheduleIndex];
				const itemScheduledPrice = item.additional_prices[itemPriceKey];

				// If the item price key is X, this means the item is unavailable to order
				// If the price does not exists in the extra prices, we throw.
				// We check for undefined since null or 0 could be accepted
				if (itemPriceKey && itemPriceKey.toLowerCase() === 'x' || typeof itemScheduledPrice === 'undefined') {
					item.unavailable = true;
					return item;
				}

				// If the default price does not match, we set the price to its scheduled price
				if (Number(item.price) !== Number(itemScheduledPrice)) {
					item.price = Number(itemScheduledPrice).toFixed(2);
				}
			}
		}
	}

	return item;
}

/**
 * Sorts an array of items by their displayed (localized) name,
 * case- and accent-insensitively, in the given direction. Does not mutate
 * the input.
 *
 * @param {MenuItem[]} items
 * @param {'asc' | 'desc'} direction
 * @return {MenuItem[]}
 */
export function sortItemsByName(items: MenuItem[], direction: 'asc' | 'desc'): MenuItem[] {
	const locale = resolveLocale(i18n.locale);
	const locales = authState.restaurant?.locales;
	const nameOf = (item: MenuItem): string => localize(item, 'name', i18n.locale, locales);
	return [...items].sort((a: MenuItem, b: MenuItem) => {
		const result = nameOf(a).localeCompare(nameOf(b), locale, { numeric: true, sensitivity: 'base' });
		return direction === 'desc' ? -result : result;
	});
}

/**
 * Splits an item's comma-separated `diets` string into a trimmed, non-empty list.
 *
 * @param {MenuItem} item
 * @return {string[]}
 */
export function parseDiets(item: MenuItem): string[] {
	return (item.diets || '')
		.split(',')
		.map((diet: string) => diet.trim())
		.filter(Boolean);
}

function createItem(i: MenuItem, m: Menu, items: MenuItem[]) {
	if(i.published_at) {
		i = getPriceAndAvailability(i) as MenuItem;
		const menuItem: MenuItem = {
			id: i.id,
			menu_group_id: m.menu_group_id,
			menu_id: m.id!,
			section_id: i.section_id,
			name: i.name,
			description: i.description,
			ingredients: i.ingredients,
			nutritional_sheet_url: i.nutritional_sheet_url,
			slug: i.slug,
			sku: i.sku,
			category: i.category,
			calories: Number.isInteger(i.calories) && i.calories! >= 0 ? i.calories : null,
			image: i.image,
			price: i.price,
			promos: i.promos,
			tax_rate: i.tax_rate,
			tax_rebate_eligibility: i.tax_rebate_eligibility,
			price_unit: i.price_unit,
			price_type: i.price_type,
			allergens: i.allergens,
			diets: i.diets,
			price_schedule: i.price_schedule,
			additional_prices: i.additional_prices,
			options: sortOption(i.options!),
			tags: i.tags,
			sold_out: i.sold_out,
			unavailable: i.unavailable,
			alcoholic_beverage: i.alcoholic_beverage,
			meal_package: i.meal_package,
			featured_item: i.featured_item,
			price_on_request: i.price_on_request,
			order: i.order,
			localization: i.localization,
			// this doesn't do anything but typescript will complain that it's missing
			auto_restock: 'disabled'
		};
		items.push(menuItem);
	}
}

export function transformMenusResponse(menus: Menu[]) {
	if (menus) {
		const formattedMenus: Menu[] = [];
		menus = orderBy(menus, ['order']);
		menus.forEach((m: Menu): void => {
			const sections: MenuSection[] = [];
			m.sections = orderBy(m.sections, ['order']);

			const menu_items: MenuItem[] = [];

			if (m.items && m.items.length){
				m.items = orderBy(m.items, ['order']);

				m.items.forEach(( i: MenuItem): void => {
					createItem(i, m, menu_items);
				});
			}
			if (m.sections){
				m.sections.forEach((s: MenuSection): void => {
					let items: MenuItem[] = [];
					s.items = orderBy(s.items || [], ['order']);

					s.items.forEach((i: MenuItem): void => {
						createItem(i, m, items);
					});
					items = orderBy(items, ['order']);
					const menuSection: MenuSection = {
						id: s.id,
						name: s.name,
						slug: s.slug,
						order: s.order,
						price: s.price,
						description: s.description,
						localization: s.localization,
						type: s.type,
						items
					};

					sections.push(menuSection);
				});
			}
			const menu: Menu = {
				name: m.name,
				id: m.id,
				slug: m.slug,
				image: m.image,
				description: m.description,
				sections: sections,
				dine_in: m.dine_in,
				on_demand: m.on_demand,
				scheduled: m.scheduled,
				member_only: m.member_only,
				event_package: m.event_package,
				access_level: m.access_level ? m.access_level : 'unrestricted',
				availability: [],
				order: m.order,
				localization: m.localization,
				menu_group_id: m.menu_group_id,
				items: menu_items,
				allow_sections: m.allow_sections,
				max_orders_per_interval: m.max_orders_per_interval,
				ordering_interval: m.ordering_interval,
				last_call_time: m.last_call_time,
				estimated_prep_time: m.estimated_prep_time,
				price: m.price,
				required_notice: m.required_notice,
				type: m.type,
				published_at: m.published_at
			};
			formattedMenus.push(menu);
		});
		return formattedMenus;
	}
	else {
		throw Error(i18n.t('orders.error_parsing_menus'));
	}
}

// =============================================================================
// General formatting
// =============================================================================
// TODO Revisit we should never use Math.round, the loss of precision can be dangerous, we should always use .toFixed(2)
export function formatValue(value: number, defaultValue: string|null, type: string, minValue: number = 0): string|number|null {
	if (!isFalseyButNotZero(value) && value >= minValue) {
		return type === 'price' ? value.toFixed(2) : Math.round(value);
	}
	return defaultValue;
}

/**
 * Return a human-readable relative time string for a given ISO date string.
 *
 * @param {string} dateString
 * @return {string}
 */
export function getTimeAgo(dateString: string): string {
	const diffInMinutes = Math.floor((Date.now() - new Date(dateString).getTime()) / 60000);

	if (diffInMinutes < 1) {
		return i18n.t('orders.dashboard.pending_orders.time_ago.just_now');
	}
	else if (diffInMinutes < 60) {
		return i18n.t('orders.dashboard.pending_orders.time_ago.minutes', { count: diffInMinutes });
	}
	else if (diffInMinutes < 1440) {
		return i18n.t('orders.dashboard.pending_orders.time_ago.hours', { count: Math.floor(diffInMinutes / 60) });
	}
	else {
		return i18n.t('orders.dashboard.pending_orders.time_ago.days', { count: Math.floor(diffInMinutes / 1440) });
	}
}

/**
 * Join an address with its extra info (unit, buzzer, etc.) when present.
 *
 * @param {string} [address]
 * @param {string} [extra]
 * @return {string | null}
 */
export function formatAddress(address?: string, extra?: string): string | null {
	if (!address) {
		return null;
	}
	return extra ? `${address}, ${extra}` : address;
}

/**
 * Format the phone number - handles North American, UK and international formats.
 * For international numbers: preserves + prefix and allows flexible formatting
 * For UK locations: groups complete mobiles (07XXX XXXXXX) and London landlines
 * (020 XXXX XXXX), otherwise keeps the user's digits and spaces as typed
 * For North American numbers: ###-###-#### format
 * Remove the +1 area code if autofill.
 *
 * @param {string} phoneNumber
 * @param {boolean} mayBeAutofill - is the input potentially from autofill
 * @return {string}
 */
export function formatPhoneNumber(phoneNumber?: string, mayBeAutofill: boolean = false): string {
	if (!phoneNumber) {
		return '';
	}

	// Handle international numbers (those starting with +)
	if (phoneNumber.startsWith('+')) {
		// For international numbers, only allow + and digits
		return '+' + phoneNumber.replace(/\D/g, '');
	}

	// Handle UK numbers
	if (getCountryFromAddress(authState.restaurant?.address) === 'GB') {
		const ukDigits = phoneNumber.replace(/\D/g, '');
		if (ukDigits.length === 11 && ukDigits.startsWith('07')) {
			return `${ukDigits.substring(0, 5)} ${ukDigits.substring(5)}`;
		}
		if (ukDigits.length === 11 && ukDigits.startsWith('020')) {
			return `${ukDigits.substring(0, 3)} ${ukDigits.substring(3, 7)} ${ukDigits.substring(7)}`;
		}
		return phoneNumber.replace(/[^\d ]/g, '');
	}

	// Handle North American numbers
	let temp = phoneNumber.replace(/\D/g, '');
	if (mayBeAutofill && temp.length === 11 && temp[0] === '1') {
		temp = temp.substring(1);
	}
	if (temp) {
		return temp.match(/\d{3}(?=\d{2,3})|\d+/g)?.join('-') || '';
	}
	return '';
}

/**
 * Normalize a stored phone value: recognizable shapes ('+'-prefixed, or bare
 * legacy NANP digits which are assumed +1) come back as compact E.164.
 * Anything else comes back as bare digits.
 *
 * @param {string} [phoneNumber]
 * @return {string} Example: '+12223334444', or '223334444' when unrecognizable
 */
function normalizePhoneNumber(phoneNumber?: string): string {
	if (!phoneNumber) {
		return '';
	}
	if (phoneNumber.startsWith('+')) {
		return '+' + phoneNumber.replace(/\D/g, '');
	}
	const digits = phoneNumber.replace(/\D/g, '');
	if (digits.length === 10) {
		return `+1${digits}`;
	}
	if (digits.length === 11 && digits.startsWith('1')) {
		return `+${digits}`;
	}
	return digits;
}

/**
 * Format a phone number for read-only display in international format.
 * Unparseable values are returned verbatim so legacy records are never destroyed in display.
 * * Same function as in Linguini
 *
 * @param {string} [phoneNumber]
 * @return {string} Example: +1 222 333 4444
 */
export function formatPhoneNumberForDisplay(phoneNumber?: string): string {
	if (!phoneNumber) {
		return '';
	}
	const e164 = normalizePhoneNumber(phoneNumber);
	if (e164.startsWith('+')) {
		const parsed = parsePhoneNumberFromString(e164);
		if (parsed?.isValid()) {
			return parsed.formatInternational();
		}
	}
	return phoneNumber;
}